//The interceptors are defined where they're used (encapsulation)
//Interceptors are initialized when the service is created
//Donot need async, functions implicitly return Promises already

import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse, AxiosError } from 'axios';
import JwtService from './jwt.service';
import { formatErrorResponse } from './error-messages';
import { useToast } from 'vuestic-ui';

class ApiService {
  private instance: AxiosInstance;
  private toast: ReturnType<typeof useToast>;

  constructor() {
    this.instance = axios.create({
      baseURL: import.meta.env.VITE_API_BASE_URL || '/',
      timeout: 300000,
      headers: {
        'Content-Type': 'application/json',
      }
    });

    this.toast = useToast();
    this.initInterceptors();
  }

  /**
   * Initialize the axios interceptors
   */
  private initInterceptors() {
    // Request interceptor
    this.instance.interceptors.request.use(
      (config) => {
        const token = JwtService.getToken();
        if (token) {
          config.headers['Authorization'] = `Bearer ${token}`;
        }
        return config;
      },
      (error) => {
        console.error('Request error:', error);
        return Promise.reject(error);
      }
    );

    // Response interceptor
    this.instance.interceptors.response.use(
      (response: AxiosResponse) => {
        return response;
      },
      (error: AxiosError) => {
        if (error.response) {
          const status = error.response.status;
          const url = error.config?.url || 'unknown endpoint';

          console.error(`API Error: ${status} for ${url}`);

          // Handle authentication errors
          if (status === 401) {
            JwtService.destroyToken();
            if (window.location.pathname !== '/auth/login') {
              window.location.href = '/auth/login';
            }
          }

          // Get the formatted error message and show notification
          const errorInfo = formatErrorResponse(error);
          const skipErrorToast = (error.config as any)?.skipErrorToast;

          if (!skipErrorToast) {
            this.toast.init({
              message: errorInfo.message,
              color: errorInfo.type === 'error' ? 'danger' : errorInfo.type === 'warning' ? 'warning' : 'info',
            });
          }

          // Return a rejected promise with the formatted error
          return Promise.reject({
            ...error,
            message: errorInfo.message,
            type: errorInfo.type
          });
        } else if (error.request) {
          console.error('No response received:', error.request);
          this.toast.init({
            message: 'No response received from server. Please check your connection.',
            color: 'danger',
          });
          return Promise.reject({
            ...error,
            message: 'No response received from server. Please check your connection.',
            type: 'error'
          });
        } else {
          console.error('Error setting up request:', error.message);
          this.toast.init({
            message: 'Error setting up request. Please try again.',
            color: 'danger',
          });
          return Promise.reject({
            ...error,
            message: 'Error setting up request. Please try again.',
            type: 'error'
          });
        }
      }
    );
  }

  /**
   * Initialize API service
   */
  init() {
    // Already handled in constructor, but keeping for backward compatibility
  }

  /**
   * Set authorization header
   */
  setHeader() {
    // Already handled in interceptors, but keeping for backward compatibility
    const token = JwtService.getToken();
    if (token) {
      this.instance.defaults.headers.common["Authorization"] = `Bearer ${token}`;
    }
  }

  /**
   * Query GET request with params
   */
  query(resource: string, params?: any) {
    return this.instance.get(resource, { params })
      .then(response => response.data)
      .catch(error => {
        // Don't wrap the error, let the interceptor handle it properly
        throw error;
      });
  }

  /**
   * GET request
   */
  get(resource: string, params?: any) {
    return this.instance.get(resource, { params })
      .then(response => response.data)
      .catch(error => {
        // Don't wrap the error, let the interceptor handle it properly
        throw error;
      });
  }

  /**
   * POST request
   */
  post(resource: string, params: any, config: AxiosRequestConfig = {}) {
    return this.instance.post(resource, params, config)
      .then(response => response.data)
      .catch(error => {
        // Don't wrap the error, let the interceptor handle it properly
        throw error;
      });
  }

  /**
   * POST multipart/form-data (file upload)
   */
  postFile(resource: string, params: any) {
    const headers = {
      'Content-Type': 'multipart/form-data'
    };
    return this.instance.post(resource, params, { headers })
      .then(response => response.data)
      .catch(error => {
        throw new Error(`API POST File Error: ${error}`);
      });
  }

  /**
   * POST form urlencoded
   */
  postForm(resource: string, params: Record<string, any>) {
    const headers = {
      'Content-Type': 'application/x-www-form-urlencoded'
    };

    // Convert to URLSearchParams if needed
    const formData = params instanceof URLSearchParams
      ? params
      : new URLSearchParams(params);

    return this.instance.post(resource, formData, { headers })
      .then(response => response.data)
      .catch(error => {
        throw new Error(`API POST Form Error: ${error}`);
      });
  }

  /**
   * PUT request
   */
  put(resource: string, params: any) {
    return this.instance.put(resource, params)
      .then(response => response.data)
      .catch(error => {
        throw new Error(`API PUT Error: ${error}`);
      });
  }

  /**
   * PATCH request
   */
  patch(resource: string, params: any) {
    return this.instance.patch(resource, params)
      .then(response => response.data)
      .catch(error => {
        throw new Error(`API PATCH Error: ${error}`);
      });
  }

  /**
   * DELETE request
   */
  delete(resource: string, config: AxiosRequestConfig = {}) {
    return this.instance.delete(resource, config)
      .then(response => response.data)
      .catch(error => {
        throw new Error(`API DELETE Error: ${error}`);
      });
  }

  /**
   * Generic request method
   */
  request<T = any>(config: AxiosRequestConfig): Promise<T> {
    return this.instance.request(config)
      .then(response => response.data)
      .catch(error => {
        throw new Error(`API Request Error: ${error}`);
      });
  }
}

// Create singleton instance
const apiService = new ApiService();

export { apiService as ApiService, JwtService };